import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import clsx from 'clsx';
import OpportunityEditor from '../components/OpportunityEditor.jsx';

const BUCKETS = [
  { id: 'stale_estimate',   label: 'Stale estimate',     hint: 'Quoted, no movement — chase authorization' },
  { id: 'waiting_parts',    label: 'Waiting on parts',   hint: 'Backordered items blocking work' },
  { id: 'partial_progress', label: 'In progress',        hint: 'Work started; check status' },
  { id: 'not_started_ro',   label: 'Not started',        hint: 'RO opened but no work logged' },
  { id: 'ready_to_invoice', label: 'Ready to invoice',   hint: 'Complete + costed but unbilled' },
  { id: 'shell_so',         label: 'Empty RO',           hint: 'No work, no $ — likely safe to close' },
];
const BUCKET_COLOR = {
  stale_estimate:   'bg-amber-50  text-amber-900  border-amber-200',
  waiting_parts:    'bg-rose-50   text-rose-900   border-rose-200',
  partial_progress: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  not_started_ro:   'bg-blue-50   text-blue-900   border-blue-200',
  ready_to_invoice: 'bg-fuchsia-50 text-fuchsia-900 border-fuchsia-200',
  shell_so:         'bg-zinc-100  text-zinc-700   border-zinc-200',
  unclassified:     'bg-panel     text-navy       border-panelDark',
};
const SORTS = [
  { id: 'date_open',           label: 'Oldest first' },
  { id: 'amt_subtotal_desc',   label: '$ Subtotal (high→low)' },
  { id: 'date_open_desc',      label: 'Newest first' },
  { id: 'cus_name',            label: 'Customer (A→Z)' },
];

function fmt$(v) {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1000) return '$' + Math.round(n).toLocaleString();
  return '$' + n.toFixed(0);
}

export default function AgingServiceOrders() {
  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('amt_subtotal_desc');
  const [openRowId, setOpenRowId] = useState(null);
  const [leadDraft, setLeadDraft] = useState(null);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (selectedBuckets.length)  p.set('hold_reason', selectedBuckets.join(','));
    if (selectedBranches.length) p.set('excede_branch_id', selectedBranches.join(','));
    if (search.trim())           p.set('search', search.trim());
    if (sort)                    p.set('sort', sort);
    p.set('limit', '200');
    return p.toString();
  }, [selectedBuckets, selectedBranches, search, sort]);

  const summary = useQuery({
    queryKey: ['aging', 'summary', selectedBranches.join(','), search],
    queryFn: () => {
      const p = new URLSearchParams();
      if (selectedBranches.length) p.set('excede_branch_id', selectedBranches.join(','));
      if (search.trim()) p.set('search', search.trim());
      return api.get(`/aging/service-orders/summary?${p.toString()}`).then(r => r.data);
    },
  });

  const list = useQuery({
    queryKey: ['aging', 'list', qs],
    queryFn: () => api.get(`/aging/service-orders?${qs}`).then(r => r.data),
    keepPreviousData: true,
  });

  const branchOptions = useMemo(() => {
    const set = new Set();
    for (const r of list.data?.data || []) set.add(r.excede_branch_id);
    // also include branches that appear in summary so toggling buckets doesn't shrink the dropdown
    return Array.from(set).sort((a, b) => a - b);
  }, [list.data]);

  function toggle(arr, setter, id) {
    setter(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  }

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Service</div>
        <h1 className="text-2xl">Aging Repair Orders</h1>
        <div className="rule-crimson" />
        <p className="text-sm text-muted mt-2">
          Open repair orders that may have slipped through the cracks. Refreshed nightly from Excede.
          Click a row for the operation/parts detail and to draft a follow-up.
        </p>
      </header>

      {/* No-notes data-quality banner (CR #47) */}
      {(summary.data?.no_notes_count || 0) > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="font-semibold">{summary.data.no_notes_count}</span> open ROs have no notes from any rep yet — these need context.
        </div>
      )}

      {/* Bucket cards (also work as filter chips) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {BUCKETS.map(b => {
          const stat = (summary.data?.buckets || []).find(x => x.hold_reason === b.id);
          const active = selectedBuckets.includes(b.id);
          return (
            <button key={b.id}
              onClick={() => toggle(selectedBuckets, setSelectedBuckets, b.id)}
              className={clsx(
                'text-left rounded border px-3 py-2 transition',
                BUCKET_COLOR[b.id],
                active ? 'ring-2 ring-crimson' : 'hover:ring-1 hover:ring-navy/40'
              )}
              title={b.hint}>
              <div className="text-xs uppercase tracking-wide opacity-75">{b.label}</div>
              <div className="text-lg font-semibold leading-tight">
                {stat ? `${stat.count}` : '0'}
                <span className="text-xs font-normal opacity-70 ml-2">{stat ? fmt$(stat.amt_subtotal_total) : '$0'}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 bg-panel rounded p-2">
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search RO#, customer, VIN, fleet#"
          className="flex-1 min-w-[12rem] px-3 py-1.5 rounded border border-panelDark text-sm bg-white"
        />
        <select value={sort} onChange={e => setSort(e.target.value)}
                className="px-2 py-1.5 rounded border border-panelDark text-sm bg-white">
          {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div className="flex flex-wrap gap-1">
          {branchOptions.map(br => (
            <button key={br} onClick={() => toggle(selectedBranches, setSelectedBranches, br)}
              className={clsx(
                'text-xs px-2 py-1 rounded border',
                selectedBranches.includes(br)
                  ? 'bg-navy text-white border-navy'
                  : 'bg-white text-navy border-panelDark hover:border-navy'
              )}>
              br {br}
            </button>
          ))}
        </div>
        {(selectedBuckets.length || selectedBranches.length || search) ? (
          <button
            onClick={() => { setSelectedBuckets([]); setSelectedBranches([]); setSearch(''); }}
            className="text-xs text-crimson hover:underline ml-auto">
            Clear filters
          </button>
        ) : null}
      </div>

      {/* Result count */}
      <div className="text-xs text-muted">
        {list.isLoading ? 'Loading…' :
          list.data?.total != null
            ? `${list.data.data.length} shown of ${list.data.total} matching`
            : ''}
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white border border-panelDark rounded">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">RO#</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Trailer</th>
              <th className="text-right px-3 py-2">Br</th>
              <th className="text-right px-3 py-2">Open</th>
              <th className="text-right px-3 py-2">Updated</th>
              <th className="text-left px-3 py-2">Bucket</th>
              <th className="text-right px-3 py-2">$ Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {(list.data?.data || []).map(r => (
              <tr key={r.id}
                  onClick={() => setOpenRowId(r.id)}
                  className="border-t border-panel hover:bg-panel/60 cursor-pointer">
                <td className="px-3 py-1.5 font-mono text-xs">{r.sls_id}</td>
                <td className="px-3 py-1.5">{r.cus_name}</td>
                <td className="px-3 py-1.5 text-xs text-muted">
                  {r.fleet_unt_id || '—'}
                  {r.vin && <span className="ml-2 opacity-70">{r.vin}</span>}
                </td>
                <td className="px-3 py-1.5 text-right text-xs">{r.excede_branch_id}</td>
                <td className="px-3 py-1.5 text-right">{r.days_open}d</td>
                <td className="px-3 py-1.5 text-right text-muted">{r.days_since_update}d</td>
                <td className="px-3 py-1.5">
                  <span className={clsx('inline-block px-2 py-0.5 rounded border text-[11px]', BUCKET_COLOR[r.hold_reason] || BUCKET_COLOR.unclassified)}>
                    {(BUCKETS.find(b => b.id === r.hold_reason)?.label) || r.hold_reason}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right">{fmt$(r.amt_subtotal)}</td>
              </tr>
            ))}
            {!list.isLoading && (list.data?.data || []).length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-6">No matching repair orders.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openRowId != null && (
        <RowDrawer
          id={openRowId}
          onClose={() => setOpenRowId(null)}
          onCreateLead={initial => { setOpenRowId(null); setLeadDraft(initial); }}
        />
      )}

      {leadDraft && (
        <OpportunityEditor
          initial={leadDraft}
          onClose={() => setLeadDraft(null)}
          onSaved={() => setLeadDraft(null)}
        />
      )}
    </div>
  );
}

function RowDrawer({ id, onClose, onCreateLead }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['aging', 'detail', id],
    queryFn: () => api.get(`/aging/service-orders/${id}`).then(r => r.data.data),
  });
  const draft = useMutation({
    mutationFn: () => api.post(`/aging/service-orders/${id}/draft-followup`).then(r => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aging', 'detail', id] });
      qc.invalidateQueries({ queryKey: ['aging', 'list'] });
    },
  });

  const r = detail.data;

  return (
    <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose}>
      <aside
        onClick={e => e.stopPropagation()}
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[640px] bg-white shadow-xl flex flex-col">
        <header className="border-b border-panel px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted">Repair Order</div>
            <div className="font-mono">{r?.sls_id || '…'}</div>
          </div>
          {r && (
            <button
              onClick={() => onCreateLead({
                name: `${r.cus_name} — service lead`,
                customer_id: r.customer_id || '',
                lead_source: 'Service referral',
                domain: 'trailer_sales',
              })}
              className="btn-primary text-xs px-2 py-1 whitespace-nowrap">
              Create Lead
            </button>
          )}
          <button onClick={onClose} className="text-navy hover:text-crimson text-xl px-2">×</button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
          {detail.isLoading && <div className="text-muted">Loading…</div>}
          {r && (
            <>
              <section>
                <div className="font-semibold">{r.cus_name}</div>
                <div className="text-xs text-muted">cus {r.cus_id} · branch {r.excede_branch_id}</div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div><span className="text-muted">Trailer:</span> {r.fleet_unt_id || '—'}</div>
                  <div><span className="text-muted">VIN:</span> {r.vin || '—'}</div>
                  <div><span className="text-muted">Opened:</span> {r.date_open?.slice(0,10)} ({r.days_open}d)</div>
                  <div><span className="text-muted">Last update:</span> {r.days_since_update}d ago</div>
                  <div><span className="text-muted">Promised:</span> {r.date_promise?.slice(0,10) || '—'}</div>
                  <div><span className="text-muted">Estimate flag:</span> {r.is_estimate ? 'yes' : 'no'}</div>
                  <div><span className="text-muted">Subtotal:</span> {fmt$(r.amt_subtotal)}</div>
                  <div><span className="text-muted">Labor / Parts:</span> {fmt$(r.amt_labor)} / {fmt$(r.amt_parts)}</div>
                </div>
              </section>

              <section>
                <div className="text-xs uppercase tracking-wide text-muted mb-1">Bucket</div>
                <div className="flex items-center gap-2">
                  <span className={clsx('inline-block px-2 py-0.5 rounded border text-xs', BUCKET_COLOR[r.hold_reason])}>
                    {(BUCKETS.find(b => b.id === r.hold_reason)?.label) || r.hold_reason}
                  </span>
                  <span className="text-xs text-muted">({r.hold_reason_confidence})</span>
                </div>
                {Array.isArray(r.hold_reason_signals) && r.hold_reason_signals.length > 0 && (
                  <ul className="text-xs text-muted mt-1 list-disc ml-5">
                    {r.hold_reason_signals.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                )}
              </section>

              <section>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-wide text-muted">Follow-up draft</div>
                  <button
                    className="btn-primary text-xs px-2 py-1"
                    disabled={draft.isPending}
                    onClick={() => draft.mutate()}>
                    {draft.isPending ? 'Drafting…' : (r.follow_up_draft ? 'Regenerate' : 'Draft with Haiku')}
                  </button>
                </div>
                {r.follow_up_draft ? (
                  <div className="bg-panel rounded px-3 py-2 text-sm">
                    {r.follow_up_draft}
                    {r.follow_up_drafted_at && (
                      <div className="text-[10px] text-muted mt-1">
                        drafted {new Date(r.follow_up_drafted_at).toLocaleString()} · {r.follow_up_drafted_by_model}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-muted">No draft yet — click above to generate.</div>
                )}
                {draft.isError && (
                  <div className="text-xs text-crimson mt-1">Draft failed: {draft.error?.response?.data?.error || draft.error?.message}</div>
                )}
              </section>

              <section>
                <div className="text-xs uppercase tracking-wide text-muted mb-1">Operations ({(r.operations_data || []).length})</div>
                <div className="space-y-1 text-xs">
                  {(r.operations_data || []).map((o, i) => (
                    <div key={i} className="border border-panel rounded px-2 py-1">
                      <div className="font-mono text-[11px] text-muted">Job {o.JobId} / Op {o.OpsId} · status {o.Status} · {o.Complete ? 'complete' : 'open'}</div>
                      {o.Condition && <div className="mt-0.5"><span className="text-muted">Condition:</span> {o.Condition}</div>}
                      {o.Cause && <div><span className="text-muted">Cause:</span> {o.Cause}</div>}
                      {o.Correction && <div><span className="text-muted">Correction:</span> {o.Correction}</div>}
                    </div>
                  ))}
                  {(r.operations_data || []).length === 0 && <div className="text-muted">(none)</div>}
                </div>
              </section>

              <section>
                <div className="text-xs uppercase tracking-wide text-muted mb-1">Items ({(r.items_data || []).length})</div>
                <div className="space-y-0.5 text-xs">
                  {(r.items_data || []).slice(0, 30).map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="text-muted w-10">Op {it.OpsId}</span>
                      <span className="flex-1 truncate">{it.Des || '(no description)'}</span>
                      {Number(it.QtyBackorder) > 0 && (
                        <span className="text-rose-700 font-semibold">BO×{it.QtyBackorder}</span>
                      )}
                      <span className="w-20 text-right">{fmt$(it.AmtPrice)}</span>
                    </div>
                  ))}
                  {(r.items_data || []).length > 30 && <div className="text-muted">…{r.items_data.length - 30} more</div>}
                </div>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
